import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  RestaurantEditResp,
  RestaurantEditVariables,
  RestaurantVariables,
  Restaurant
} from "../../types/restaurantTypes";

const UPDATE_RESTAURANT = gql`
  mutation UpdateRestaurant($attributes: UpdateRestaurantInput!) {
    updateRestaurant(attributes: $attributes) {
      restaurant {
        id
        name
        phone
        website
        image
        wifi
      }
      errors
    }
  }
`   

type UseUpdateRestaurantOptions = {
  onCompleted?: (restaurant: Restaurant) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<RestaurantEditResp, RestaurantEditVariables>>[1];
};

const useUpdateRestaurant = (options?: UseUpdateRestaurantOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    RestaurantEditResp,
    RestaurantEditVariables
  >(UPDATE_RESTAURANT, options?.mutationOptions);

  const updateRestaurant = useCallback(
    async (input: RestaurantVariables & { id: string }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              phone: input.phone,
              website: input.website,
              name: input.name,
              image: input.image,
              wifi: input.wifi,
              address: {
                fullAddress: input.address.fullAddress,
                latitude: input.address.latitude,
                longitude: input.address.longitude,
                street: input.address.street,
                house: input.address.house,
                countryCode: input.address.countryCode,
                mapboxId: input.address.mapboxId
              }
            },
          },
        });

        const updateRestaurant = result?.data?.updateRestaurant;

        if (updateRestaurant?.errors?.length) {
          throw new Error(updateRestaurant.errors.join(", "));
        }

        if (options?.onCompleted && updateRestaurant?.restaurant) {
          options.onCompleted(updateRestaurant.restaurant);
        }

        return updateRestaurant?.restaurant;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const restaurant = data?.updateRestaurant?.restaurant;

  return {
    updateRestaurant,
    updatedRestaurant: restaurant,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateRestaurant
